/**
 * GroupCoursePreviewPage.jsx - Sprint 3
 * 
 * Página de preview del curso grupal completo antes de publicar.
 * Simula la vista que tendrán los estudiantes, permitiendo navegar por semanas
 * y ver todos los bloques configurados.
 * 
 * @see PLAN_GROUPCOURSE_BUILDER.md - Sprint 3, Día 16
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Edit,
  CheckCircle2,
  Eye,
  Users,
  Calendar,
  FileText,
  List,
  ClipboardCheck,
  Brain,
  FileCheck,
  Link as LinkIcon,
  Youtube,
  FileType
} from 'lucide-react';
import apiClient from '../../services/apiClient';
import { validateGroupCourse } from '../../services/courseValidationService';
import PublishValidationReport from './PublishValidationReport';

const GroupCoursePreviewPage = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  
  const [course, setCourse] = useState(null);
  const [selectedWeek, setSelectedWeek] = useState(1);
  const [loading, setLoading] = useState(true);
  const [showValidationReport, setShowValidationReport] = useState(false);
  const [validationResult, setValidationResult] = useState(null);

  useEffect(() => {
    loadCourse();
  }, [courseId]);

  const loadCourse = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/admin/group-courses/${courseId}`);
      setCourse(response.data);
    } catch (error) {
      console.error('Error cargando curso:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleValidate = () => {
    if (!course) return;
    const result = validateGroupCourse(course);
    setValidationResult(result);
    setShowValidationReport(true);
  };

  const handlePublish = async () => {
    try {
      await apiClient.post(`/admin/group-courses/${courseId}/publish`);
      alert('Curso publicado exitosamente');
      navigate('/admin/group-courses');
    } catch (error) {
      console.error('Error publicando curso:', error);
      alert('Error al publicar el curso');
    }
  };

  const renderResource = (resource) => {
    const iconMap = {
      youtube: <Youtube className="w-5 h-5 text-red-600" />,
      pdf: <FileType className="w-5 h-5 text-red-600" />,
      link: <LinkIcon className="w-5 h-5 text-blue-600" />,
      checklist: <CheckCircle2 className="w-5 h-5 text-green-600" />
    };

    return (
      <div key={resource.id} className="flex items-start gap-3 p-3 bg-white border border-gray-200 rounded-lg hover:border-blue-300 transition-colors">
        <div className="flex-shrink-0 mt-0.5">
          {iconMap[resource.type]}
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-medium text-gray-900">{resource.title}</h4>
          {resource.description && (
            <p className="text-sm text-gray-600 mt-1">{resource.description}</p>
          )}
          {resource.url && (
            <a 
              href={resource.url} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-sm text-blue-600 hover:underline mt-1 inline-block"
            >
              Abrir recurso →
            </a>
          )}
          {resource.items && resource.items.length > 0 && (
            <ul className="mt-2 space-y-1">
              {resource.items.map((item, idx) => (
                <li key={idx} className="text-sm text-gray-700 flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-gray-400"></div>
                  {item}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    );
  };

  const renderField = (field) => {
    const typeLabels = {
      text: '📝 Texto corto',
      textarea: '📄 Texto largo',
      number: '🔢 Número',
      select: '☑️ Selección',
      url: '🔗 URL',
      date: '📅 Fecha'
    };

    return (
      <div key={field.id} className="border border-gray-200 rounded-lg p-3 bg-white">
        <label className="block text-sm font-medium text-gray-900 mb-2">
          {field.label}
          {field.required && <span className="text-red-500 ml-1">*</span>}
          <span className="ml-2 text-xs text-gray-500">{typeLabels[field.type]}</span>
        </label>
        {field.placeholder && (
          <p className="text-sm text-gray-500 mb-2">{field.placeholder}</p>
        )}
        {field.type === 'textarea' ? (
          <textarea
            placeholder="El estudiante escribirá aquí..."
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
            disabled
          />
        ) : field.type === 'select' ? (
          <select className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50" disabled>
            <option>Selecciona una opción</option>
            {field.options?.map((opt, i) => (
              <option key={i}>{opt}</option>
            ))}
          </select>
        ) : (
          <input
            type={field.type === 'number' ? 'number' : field.type === 'url' ? 'url' : field.type === 'date' ? 'date' : 'text'}
            placeholder="El estudiante completará este campo..."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
            disabled
          />
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-600 border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando curso...</p>
        </div>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-red-600">Error: No se pudo cargar el curso</p>
          <button
            onClick={() => navigate('/admin/group-courses')}
            className="mt-4 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          >
            Volver al listado
          </button>
        </div>
      </div>
    );
  }

  const currentWeek = course.weeks?.find(w => w.weekNumber === selectedWeek);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Toolbar Superior */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/admin/group-courses')}
                className="text-gray-600 hover:text-gray-900 flex items-center gap-2"
              >
                <ArrowLeft className="w-5 h-5" />
                Volver
              </button>
              <div className="border-l border-gray-300 h-6"></div>
              <div className="flex items-center gap-3">
                <Eye className="w-6 h-6 text-purple-600" />
                <div>
                  <h1 className="text-lg font-semibold text-gray-900">{course.title}</h1>
                  <p className="text-sm text-gray-600">Vista Previa del Curso</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate(`/admin/group-courses/${courseId}/edit`)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg flex items-center gap-2 transition-colors"
              >
                <Edit className="w-4 h-4" />
                Volver a Editar
              </button>
              <button
                onClick={handleValidate}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 transition-colors"
              >
                <CheckCircle2 className="w-4 h-4" />
                Validar Curso
              </button>
              <button
                onClick={handlePublish}
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold flex items-center gap-2 transition-colors"
              >
                <CheckCircle2 className="w-4 h-4" />
                Publicar Curso
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Contenido Principal */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-12 gap-6">
          {/* Sidebar: Navegación Semanal */}
          <div className="col-span-3">
            <div className="bg-white rounded-lg border border-gray-200 p-4 sticky top-24">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-purple-600" />
                Semanas ({course.weeks?.length || 0})
              </h3>
              <div className="space-y-2">
                {course.weeks?.map((week) => (
                  <button
                    key={week.weekNumber}
                    onClick={() => setSelectedWeek(week.weekNumber)}
                    className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                      selectedWeek === week.weekNumber
                        ? 'bg-purple-100 text-purple-900 font-semibold'
                        : 'hover:bg-gray-100 text-gray-700'
                    }`}
                  >
                    Semana {week.weekNumber}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Contenido de la Semana */}
          <div className="col-span-9">
            {currentWeek ? (
              <div className="space-y-6">
                {/* Header de Semana */}
                <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-lg p-6 text-white">
                  <h2 className="text-2xl font-bold mb-2">Semana {currentWeek.weekNumber}</h2>
                  <p className="text-purple-100">
                    Vista previa de todos los bloques configurados para esta semana
                  </p>
                </div>

                {/* B1: Recursos */}
                {currentWeek.b1_resources && currentWeek.b1_resources.length > 0 && (
                  <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <FileText className="w-5 h-5 text-blue-600" />
                      B1: Recursos de Aprendizaje
                    </h3>
                    <div className="space-y-3">
                      {currentWeek.b1_resources.map(renderResource)}
                    </div>
                  </div>
                )}

                {/* B2: Template de Ejecución */}
                {currentWeek.b2_execution_template && currentWeek.b2_execution_template.fields && currentWeek.b2_execution_template.fields.length > 0 && (
                  <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <List className="w-5 h-5 text-green-600" />
                      B2: Template de Ejecución
                      <span className="text-red-500">*</span>
                    </h3>
                    {currentWeek.b2_execution_template.instructions && (
                      <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <p className="text-sm text-blue-800">{currentWeek.b2_execution_template.instructions}</p>
                      </div>
                    )}
                    <div className="space-y-3">
                      {currentWeek.b2_execution_template.fields.map(renderField)}
                    </div>
                  </div>
                )}

                {/* B3: Criterios de Auditoría */}
                {currentWeek.b3_criteria && currentWeek.b3_criteria.length > 0 && (
                  <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <ClipboardCheck className="w-5 h-5 text-yellow-600" />
                      B3: Criterios de Evaluación (Profesor)
                    </h3>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50 border-b">
                          <tr>
                            <th className="px-4 py-2 text-left font-semibold text-gray-900">Criterio</th>
                            <th className="px-4 py-2 text-left font-semibold text-gray-900">Descripción</th>
                            <th className="px-4 py-2 text-center font-semibold text-gray-900">Peso</th>
                          </tr>
                        </thead>
                        <tbody>
                          {currentWeek.b3_criteria.map((criterion, idx) => (
                            <tr key={idx} className="border-b hover:bg-gray-50">
                              <td className="px-4 py-3 font-medium text-gray-900">{criterion.name}</td>
                              <td className="px-4 py-3 text-gray-600">{criterion.description}</td>
                              <td className="px-4 py-3 text-center">
                                <span className="inline-flex items-center px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full font-semibold">
                                  {criterion.weight}%
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* B4: Coach IA */}
                {currentWeek.b4_coach_config && currentWeek.b4_coach_config.enabled && (
                  <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <Brain className="w-5 h-5 text-purple-600" />
                      B4: Configuración Coach IA
                    </h3>
                    <div className="space-y-3 text-sm">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-700">Estado:</span>
                        <span className="text-green-600 font-semibold">✓ Habilitado</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-700">Frecuencia:</span>
                        <span className="text-gray-900">{currentWeek.b4_coach_config.evaluationFrequency}</span>
                      </div>
                      {currentWeek.b4_coach_config.focusAreas && currentWeek.b4_coach_config.focusAreas.length > 0 && (
                        <div>
                          <span className="font-medium text-gray-700">Áreas de enfoque:</span>
                          <div className="flex flex-wrap gap-2 mt-2">
                            {currentWeek.b4_coach_config.focusAreas.map((area, idx) => (
                              <span key={idx} className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs">
                                {area}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* B5: Template de Formalización */}
                {currentWeek.b5_formalization_template && (
                  <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <FileCheck className="w-5 h-5 text-indigo-600" />
                      B5: Template de Formalización
                    </h3>
                    <div className="text-sm">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="font-medium text-gray-700">Formato:</span>
                        <span className="px-2 py-1 bg-indigo-100 text-indigo-700 rounded">
                          {currentWeek.b5_formalization_template.format}
                        </span>
                      </div>
                      {currentWeek.b5_formalization_template.format === 'structured' && currentWeek.b5_formalization_template.sections && (
                        <p className="text-gray-600">{currentWeek.b5_formalization_template.sections.length} sección(es) definida(s)</p>
                      )}
                    </div>
                  </div>
                )}

                {/* B6: Cross-Feedback */}
                {currentWeek.b6_criteria && currentWeek.b6_criteria.length > 0 && (
                  <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <Users className="w-5 h-5 text-pink-600" />
                      B6: Criterios de Cross-Feedback (Pares)
                    </h3>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50 border-b">
                          <tr>
                            <th className="px-4 py-2 text-left font-semibold text-gray-900">Criterio</th>
                            <th className="px-4 py-2 text-left font-semibold text-gray-900">Descripción</th>
                            <th className="px-4 py-2 text-center font-semibold text-gray-900">Peso</th>
                          </tr>
                        </thead>
                        <tbody>
                          {currentWeek.b6_criteria.map((criterion, idx) => (
                            <tr key={idx} className="border-b hover:bg-gray-50">
                              <td className="px-4 py-3 font-medium text-gray-900">{criterion.name}</td>
                              <td className="px-4 py-3 text-gray-600">{criterion.description}</td>
                              <td className="px-4 py-3 text-center">
                                <span className="inline-flex items-center px-2 py-1 bg-pink-100 text-pink-800 rounded-full font-semibold">
                                  {criterion.weight}%
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
                <p className="text-gray-600">Semana no encontrada</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal de Reporte de Validación */}
      {showValidationReport && validationResult && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="w-full max-w-4xl">
            <PublishValidationReport
              validationResult={validationResult}
              onClose={() => setShowValidationReport(false)}
              onPublish={handlePublish}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default GroupCoursePreviewPage;
